import type React from 'react';
import { Suspense, useEffect } from 'react';

import { roomUuidSelector } from '@redux/room/selectors';
import useSelector from '@redux/useSelector';
import { Auth, Hub } from 'aws-amplify';
import { useLocation } from 'react-router-dom';
import { useBoolean } from 'react-use';

import Loader from 'components/shared/Loader';
import { AUTH_CHANNEL, AUTH_EVENT } from 'constants/amplify';
import { PATHS } from 'constants/pathsB2b';
import { lazyWithRetry } from 'services/helpers/lazyWithRetry';
import { useB2bAuthEventListeners } from 'services/hooks/useB2bAuthEventListeners';
import { useTriggerSignOut } from 'services/hooks/useTriggerSignOut';
import type { HubCallback } from 'shared-types/amplify';

const Authentication = lazyWithRetry(() => import('components/b2b/Authentication'));

const NON_AUTHENTICATED_PATHS = [
  PATHS.PLAYER,
  PATHS.DESKTOP_PLAYER,
  PATHS.PLAYER_SESSION_END,
  PATHS.CATALOG,
];

export const withAuthentication = <T extends object>(App: React.FunctionComponent<T>) => {
  const AppWithAuthentication: React.FunctionComponent<T> = (props: T) => {
    const { pathname } = useLocation();
    const [signedIn, setSignedIn] = useBoolean(false);
    const { handleSignIn, handleSignOut } = useB2bAuthEventListeners();
    const storedRoomUuid = useSelector(roomUuidSelector);

    useTriggerSignOut();

    useEffect(() => {
      const checkB2bRoom = async () => {
        try {
          const b2bRoom = await Auth.currentAuthenticatedUser();
          if (b2bRoom) {
            setSignedIn(true);
          }
        } catch {
          setSignedIn(false);
        }
      };
      checkB2bRoom();
    }, [setSignedIn]);

    useEffect(() => {
      const authListener: HubCallback = async ({ payload }) => {
        const eventRoomUuid = payload.data?.username;

        switch (payload.event) {
          case AUTH_EVENT.SIGN_IN:
            setSignedIn(true);
            handleSignIn(eventRoomUuid);
            break;
          case AUTH_EVENT.SIGN_OUT: {
            const roomUuid = eventRoomUuid || storedRoomUuid;
            setSignedIn(false);
            handleSignOut(roomUuid);
            break;
          }
          default:
            break;
        }
      };
      Hub.listen(AUTH_CHANNEL, authListener);
      return () => Hub.remove(AUTH_CHANNEL, authListener);
    }, [storedRoomUuid, setSignedIn, handleSignIn, handleSignOut]);
    const isACatalogPath = pathname.startsWith('/catalog');
    const canAccessApp = NON_AUTHENTICATED_PATHS.includes(pathname) || isACatalogPath || signedIn;
    if (canAccessApp) {
      return <App {...props} />;
    }

    return (
      <Suspense fallback={<Loader />}>
        <Authentication />
      </Suspense>
    );
  };

  return AppWithAuthentication;
};
